import { useMemo } from "react";

import useGetProductById from "@/hooks/requests/product/use-get-product-by-id";
import { parseVariationsFromString } from "@/helpers/variation-helpers";
import { ISku, IVariation } from "@/interfaces/sku.interface";

export const getSkuVariations = (sku: Pick<ISku, "variations">): IVariation[] => {
  if (!sku?.variations) return [];
  if (typeof sku.variations === "string") return parseVariationsFromString(sku.variations);
  return sku.variations as IVariation[];
};

export const getVariationValue = (sku: ISku, variationName: string): string => {
  const found = getSkuVariations(sku).find((v) => v.VariationName === variationName);
  return found?.VariationValue ?? "";
};

export const getSkuStock = (sku: Pick<ISku, "stock">): number => Number(sku?.stock) || 0;

export const getActiveSkus = (skus: ISku[] | null | undefined): ISku[] =>
  (skus || []).filter((sku) => sku && sku.active !== false);

export const hasSkuStock = (sku: Pick<ISku, "stock">): boolean => getSkuStock(sku) > 0;

export const productHasStock = (product: { skus?: ISku[] | null } | null | undefined): boolean =>
  getActiveSkus(product?.skus).some(hasSkuStock);

/**
 * Indica se algum SKU compatível com as variações anteriores e com o valor informado tem saldo.
 */
export function variationOptionHasStock(
  skus: ISku[],
  variationName: string,
  value: string,
  selected: Record<string, string>,
  variationNames: string[]
): boolean {
  const previous = variationNames.slice(0, variationNames.indexOf(variationName));

  return skus.some(
    (sku) =>
      getVariationValue(sku, variationName) === value &&
      previous.every((name) => !selected[name] || getVariationValue(sku, name) === selected[name]) &&
      hasSkuStock(sku)
  );
}

type UseProductSkusProps = {
  productId?: number | null;
};

type UseProductSkusReturn = {
  isLoading: boolean;
  skus: ISku[];
  variationNames: string[];
};

/**
 * Carrega os SKUs ativos do produto selecionado e deriva os nomes de variação existentes.
 */
export function useProductSkus({ productId }: UseProductSkusProps): UseProductSkusReturn {
  const { data: product, isLoading } = useGetProductById({
    _id: Number(productId),
    enabled: !!productId,
  });

  const skus = useMemo<ISku[]>(() => getActiveSkus(product?.skus as ISku[]), [product]);

  const variationNames = useMemo<string[]>(() => {
    const names: string[] = [];
    skus.forEach((sku) => {
      getSkuVariations(sku).forEach((variation) => {
        if (variation.VariationName && !names.includes(variation.VariationName)) {
          names.push(variation.VariationName);
        }
      });
    });
    return names;
  }, [skus]);

  return { isLoading, skus, variationNames };
}

/**
 * Opções distintas para uma variação, considerando as variações já escolhidas.
 */
export function getVariationOptions(
  skus: ISku[],
  variationName: string,
  selected: Record<string, string>,
  variationNames: string[]
): string[] {
  const index = variationNames.indexOf(variationName);
  const previous = variationNames.slice(0, index);

  const filtered = skus.filter((sku) =>
    previous.every((name) => !selected[name] || getVariationValue(sku, name) === selected[name])
  );

  const values: string[] = [];
  filtered.forEach((sku) => {
    const value = getVariationValue(sku, variationName);
    if (value && !values.includes(value)) values.push(value);
  });

  return values;
}

export function findMatchingSku(
  skus: ISku[],
  variationNames: string[],
  selected: Record<string, string>
): ISku | null {
  if (variationNames.length === 0) {
    return skus.length === 1 ? skus[0] : null;
  }

  const allSelected = variationNames.every((name) => !!selected[name]);
  if (!allSelected) return null;

  const matches = skus.filter((sku) =>
    variationNames.every((name) => getVariationValue(sku, name) === selected[name])
  );

  return matches.length > 0 ? matches[0] : null;
}

export function getSkuUnitPrice(sku: ISku): number {
  const promoPrice = Number(sku.promoPrice) || 0;
  const mainPrice = Number(sku.mainPrice) || 0;

  if (promoPrice > 0 && sku.startPromoPrice && sku.endPromoPrice) {
    const today = new Date();
    const start = new Date(sku.startPromoPrice);
    const end = new Date(sku.endPromoPrice);
    if (today >= start && today <= end) return promoPrice;
  }

  return mainPrice;
}
